import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Comment } from '../../contexts/PostContext';
import { useAuth } from '../../contexts/AuthContext';

interface CommentSectionProps {
  comments: Comment[];
  postId: string;
  onAddComment: (postId: string, comment: { userId: string; username: string; userPhotoURL: string; content: string }) => Promise<void>;
  onDeleteComment: (postId: string, commentId: string) => Promise<void>;
}

const CommentSection: React.FC<CommentSectionProps> = ({ comments, postId, onAddComment, onDeleteComment }) => {
  const { currentUser } = useAuth();
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser || !commentText.trim()) return;

    try {
      setIsSubmitting(true);
      await onAddComment(postId, {
        userId: currentUser.id,
        username: currentUser.username,
        userPhotoURL: currentUser.photoURL,
        content: commentText.trim()
      });
      setCommentText('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!currentUser || !window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      setDeletingCommentId(commentId);
      await onDeleteComment(postId, commentId);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <div className="mt-6">
      <h3 className="text-xl font-semibold text-white mb-4">Comments</h3>
      
      {currentUser && (
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="flex items-start space-x-3">
            <img 
              src={currentUser.photoURL} 
              alt={currentUser.username}
              className="w-10 h-10 rounded-full object-cover border border-gray-700"
            />
            <div className="flex-1 relative">
              <textarea
                id="comment-input"
                name="comment"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                className="w-full px-4 py-2 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px] pr-12 border border-[#404040] hover:border-[#505050] transition-colors"
                maxLength={500}
                aria-label="Write a comment"
              />
              <button
                type="submit"
                disabled={isSubmitting || !commentText.trim()}
                className="absolute bottom-3 right-3 text-purple-500 hover:text-purple-400 disabled:text-gray-500 disabled:cursor-not-allowed px-3 py-1"
                aria-label="Post comment"
              >
                Share
              </button>
            </div>
          </div>
        </form>
      )}
      
      <div className="space-y-4">
        {comments.length === 0 ? (
          <p className="text-gray-400 text-center py-4">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map((comment) => {
            const isCommentOwner = currentUser?.id === comment.userId;

            return (
              <div key={comment.id} className="flex space-x-3">
                <img
                  src={comment.userPhotoURL}
                  alt={comment.username}
                  className="w-10 h-10 rounded-full object-cover border border-gray-700 mt-1"
                />
                <div className="flex-1">
                  <div className="bg-[#272727] rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium text-white">{comment.username}</div>
                      <div className="flex items-center space-x-2">
                        <div className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                        </div>
                        {isCommentOwner && (
                          <button
                            onClick={() => handleDelete(comment.id)}
                            disabled={deletingCommentId === comment.id}
                            className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-300">{comment.content}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CommentSection;